/**
 * NoteEditorModal (OPS 3.8 step 1) — the one shared note editor. Icon-triggered
 * modal (repo modal-shell pattern) with title/body fields and explicit
 * Save/Cancel — replaces save-on-blur and inline flat forms.
 *
 * Draft discipline: the draft survives a failed save (error shown inline,
 * fields untouched) and is reset only after a successful save, which also
 * closes the modal. Cancel discards without saving.
 */

import { useId, useState } from 'react';

export function NoteEditorModal({ heading, contextLabel, onClose, onSave }: {
  heading: string;
  /** Optional context line, e.g. "Checklist item: Trade name reservation". */
  contextLabel?: string;
  onClose: () => void;
  /** Must throw on failure — the draft is preserved and the error shown. */
  onSave: (input: { title?: string; bodyMarkdown: string }) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uid = useId();
  const titleId = `${uid}-title`;
  const bodyId = `${uid}-body`;

  const save = async () => {
    if (!body.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSave({ title: title.trim() || undefined, bodyMarkdown: body.trim() });
      setTitle('');
      setBody('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label={heading}>
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-lg mx-4 shadow-2xl animate-pop p-5 max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-base font-semibold text-white">{heading}</h2>
          <button onClick={onClose} aria-label="Close" className="text-muted hover:text-white p-2 -m-1">✕</button>
        </div>
        {contextLabel && <p className="text-xs text-accent mb-3">{contextLabel}</p>}

        <div className="space-y-3 mt-3">
          <div>
            <label htmlFor={titleId} className="block text-xs text-muted mb-1">Title (optional)</label>
            <input
              id={titleId}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
            />
          </div>
          <div>
            <label htmlFor={bodyId} className="block text-xs text-muted mb-1">Note</label>
            <textarea
              id={bodyId}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Note body (markdown supported)"
              rows={4}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent resize-none"
            />
          </div>
          {error && <p className="text-xs text-rose-400">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} disabled={busy} className="text-sm text-muted hover:text-white px-3 py-2 disabled:opacity-50">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || !body.trim()}
              className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-2 rounded-lg"
            >
              {busy ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
